import { useCallback, useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { CONTRACTS_API_URL } from '../../constants';

type AuthFetch = (url: string, init?: RequestInit) => Promise<Response>;

interface FormulaRow {
  id: string;
  name: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  product?: string | null;
  gravityMin?: number | null;
  gravityMax?: number | null;
  isDefault: boolean;
}

interface ComputeResult {
  formulaName: string;
  unitPrice: number;
  amount: number;
}

interface OfftakePricingPanelProps {
  contractId: string;
  authFetch: AuthFetch;
  canCompute?: boolean;
  defaultDate?: string;
  onError?: (message: string) => void;
}

async function readJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    throw new Error((await res.text()) || `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

function money(value: number): string {
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export function OfftakePricingPanel({
  contractId,
  authFetch,
  canCompute = true,
  defaultDate = '2026-09-01',
  onError
}: OfftakePricingPanelProps): ReactElement {
  const [formulas, setFormulas] = useState<FormulaRow[]>([]);
  const [productionDate, setProductionDate] = useState(defaultDate);
  const [gravity, setGravity] = useState('');
  const [quantity, setQuantity] = useState('1000');
  const [result, setResult] = useState<ComputeResult | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setFormulas(await readJson<FormulaRow[]>(await authFetch(`${CONTRACTS_API_URL}/${contractId}/price-formulas`)));
  }, [authFetch, contractId]);

  useEffect(() => {
    void load().catch((e) => onError?.(e instanceof Error ? e.message : 'Could not load offtake formulas'));
  }, [load, onError]);

  const onCompute = async (e: FormEvent) => {
    e.preventDefault();
    onError?.('');
    setBusy(true);
    try {
      const computed = await readJson<ComputeResult>(
        await authFetch(`${CONTRACTS_API_URL}/${contractId}/price-formulas/compute`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            productionDate,
            product: 'OIL',
            gravity: gravity ? Number(gravity) : null,
            quantity: Number(quantity)
          })
        })
      );
      setResult(computed);
    } catch (err) {
      setResult(null);
      onError?.(err instanceof Error ? err.message : 'Could not compute offtake price');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="nested-card" aria-label="Offtake pricing" data-testid="offtake-pricing">
      <h4 className="form-section-title">Offtake pricing</h4>
      <p className="hint">
        Settlement is the in-force T-20 formula times loaded NYMEX / Argus / Platts quotes. Amount is quantity × $/bbl.
      </p>
      {formulas.length === 0 ? (
        <p className="hint">No formulas on this offtake.</p>
      ) : (
        <table className="po-lines" data-testid="offtake-formula-grid">
          <thead>
            <tr>
              <th>Formula</th>
              <th>In force</th>
              <th>Picker</th>
            </tr>
          </thead>
          <tbody>
            {formulas.map((row) => (
              <tr key={row.id}>
                <td>{row.name}</td>
                <td>
                  {row.effectiveFrom}
                  {row.effectiveTo ? ` – ${row.effectiveTo}` : ' onward'}
                </td>
                <td>
                  {row.isDefault
                    ? 'Default'
                    : row.gravityMin != null
                      ? `Gravity ${row.gravityMin}–${row.gravityMax}`
                      : row.product || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {canCompute && (
        <form className="grid" onSubmit={(e) => void onCompute(e)} data-testid="offtake-compute-form">
          <label>
            Production date
            <input
              type="date"
              data-testid="offtake-compute-date"
              value={productionDate}
              onChange={(e) => setProductionDate(e.target.value)}
            />
          </label>
          <label>
            API gravity
            <input
              data-testid="offtake-compute-gravity"
              value={gravity}
              onChange={(e) => setGravity(e.target.value)}
              placeholder="blank = Default"
            />
          </label>
          <label>
            Quantity (bbl)
            <input
              data-testid="offtake-compute-qty"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </label>
          <button type="submit" data-testid="offtake-compute" disabled={busy}>
            {busy ? 'Computing…' : 'Compute'}
          </button>
        </form>
      )}
      {result && (
        <dl className="detail-list" data-testid="offtake-compute-result">
          <dt>Formula</dt>
          <dd data-testid="offtake-formula-name">{result.formulaName}</dd>
          <dt>$/bbl</dt>
          <dd data-testid="offtake-unit-price">{money(result.unitPrice)}</dd>
          <dt>Amount</dt>
          <dd data-testid="offtake-amount">{money(result.amount)}</dd>
        </dl>
      )}
    </section>
  );
}
